"use client";
 
import { 
  motion,
  useScroll,
  useTransform,
  Variants,
} from "motion/react";
import { Menu, X, User, ShoppingBag, MapPin, Phone, Clock } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

import Image from "next/image";
// Animation variants with proper typing
const NAVBAR_VARIANTS: Variants = {
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.42, 0, 0.58, 1] as const,
    },
  },
  hidden: {
    y: -100,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: [0.42, 0, 0.58, 1] as const,
    },
  },
};

const MOBILE_DRAWER_VARIANTS: Variants = {
  closed: {
    x: "100%",
    transition: {
      duration: 0.3,
      ease: "easeInOut",
      when: "afterChildren",
    },
  },
  open: {
    x: "0%",
    transition: {
      duration: 0.3,
      ease: "easeInOut",
      when: "beforeChildren",
      staggerChildren: 0.05,
    },
  },
};

const MOBILE_ITEM_VARIANTS: Variants = {
  closed: {
    opacity: 0,
    x: 20,
  },
  open: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.2,
      ease: [0.42, 0, 0.58, 1] as const,
    },
  },
};

const BACKDROP_VARIANTS: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: "easeIn",
    },
  },
};

// Navigation items
const NAV_ITEMS = [
  { name: "Home", href: "/", icon: null },
  { name: "Discover", href: "/dashboards/customer", icon: null },
  { name: "IndePay", href: "/dashboards/customer/inde_pay", icon: ShoppingBag },
  { name: "About", href: "/pages/about", icon: null },
  { name: "Contact", href: "/pages/contact", icon: Phone }, 
] as const;
 
export function MainNavigation( ) {
  // State management
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Hooks
  const pathname = usePathname();
  const { scrollY } = useScroll();
  const lastScrollY = useRef(0);
  const [isScrollingUp, setIsScrollingUp] = useState(true);
  const scrollStartY = useRef(0);

  // Scroll transforms
  const navbarOpacity = useTransform(scrollY, [0, 100], [0.95, 0.98]);
  const navbarBlur = useTransform(scrollY, [0, 50], [8, 16]);

  // Handle scroll behavior and auto-close mobile menu
  useEffect(() => {
    const updateScrollDirection = (current: number) => {
      const isScrollingDown = current > lastScrollY.current && current > 100;
      setIsScrollingUp(!isScrollingDown);
      setIsScrolled(current > 20);

      // Auto-close mobile menu when scrolling
      if (isMobileMenuOpen) {
        const scrollDifference = Math.abs(current - scrollStartY.current);
        if (scrollDifference > 50) {
          // Close menu after 50px scroll
          setIsMobileMenuOpen(false);
        }
      }

      lastScrollY.current = current;
    };

    const unsubscribe = scrollY.on("change", updateScrollDirection);
    return () => unsubscribe();
  }, [scrollY, isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
      scrollStartY.current = scrollY.get();
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen, scrollY]);

  // Memoized handlers
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const isActiveLink = useCallback(
    (href: string) => {
      if (href === "/") return pathname === "/";
      return pathname.startsWith(href);
    },
    [pathname]
  );

  return (
    <>
      <motion.header
        className={[
          "sticky top-0 z-50 w-full transition-all duration-300",
          "backdrop-blur-md border-b",
          isScrolled && "shadow-md",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          backdropFilter: `blur(${navbarBlur.get()}px)`,
          backgroundColor: `rgba(250, 250, 250, ${navbarOpacity.get()})`,
          borderBottomColor: `rgba(47, 72, 88, 0.1)`,
        }}
        initial="visible"
        animate={isScrollingUp ? "visible" : "hidden"}
        variants={NAVBAR_VARIANTS}
      >
        {/* Top info bar - hidden on mobile */}
        <div
          className="hidden lg:block py-1"
          style={{ backgroundColor: `rgba(47, 72, 88, 0.05)` }}
        >
          <div
            className="container mx-auto px-4 flex justify-between items-center text-xs"
            style={{ color: "#7B7B7B" }}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span>Lilongwe, Malawi</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>Open: 6AM - 11PM</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              <span>+265 123 456 789</span>
            </div>
          </div>
        </div>

        <nav
          className="container mx-auto px-4 sm:px-6 lg:px-8"
          role="navigation"
          aria-label="Main navigation"
        >
          <div className="flex h-16 md:h-18 items-center justify-between">
            {/* Logo Section */}
            <div className="flex-shrink-0">
              <Link href="/" className="flex items-center gap-2">
                <motion.div
                  whileHover={{ scale: 1.05, rotate: 3 }}
                  whileTap={{ scale: 0.95, rotate: -3 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  className="h-15 w-50 rounded-full overflow-hidden flex items-center justify-center shadow-sm"
                >
                  <Image
                    src="/logo.svg"
                    alt="Inde Menu"
                    width={120}
                    height={120}
                    className="object-contain"
                  />
                </motion.div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center justify-center flex-1 max-w-md mx-8">
              <div
                className="flex items-center space-x-1 lg:space-x-2 rounded-full p-1"
                style={{ backgroundColor: `rgba(250, 250, 250, 0.8)` }}
              >
                {NAV_ITEMS.map((item) => {
                  const isActive = isActiveLink(item.href);
                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={`
                    relative px-3 lg:px-4 py-2 text-sm font-medium rounded-full transition-all duration-200
                    hover:shadow-sm
                    ${isActive ? `shadow-sm` : `hover:text-opacity-80`}`}
                      style={{
                        backgroundColor: isActive ? "#FFFFFF" : "transparent",
                        color: isActive ? "#2F4858" : "#1E1E1E",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor =
                            "rgba(255, 255, 255, 0.7)";
                          e.currentTarget.style.color = "#2F4858";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "#1E1E1E";
                        }
                      }}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <span className="flex items-center gap-1.5">
                        {item.icon && <item.icon className="h-4 w-4" />}
                        {item.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-2 lg:space-x-4">
              {/* Login Button */}
              <Link
                href="/login"
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors duration-200"
                style={{ color: "#1E1E1E" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#2F4858";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#1E1E1E";
                }}
              >
                <User className="h-4 w-4" />
                <span className="hidden lg:inline">Login</span>
              </Link>

              {/* CTA Button */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  href="/vendors"
                  className="inline-flex items-center gap-2 px-4 lg:px-6 py-2 rounded-full font-semibold text-sm transition-all duration-200 shadow-sm hover:shadow-md text-white"
                  style={{
                    backgroundColor: "#FF8C42",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255, 140, 66, 0.9)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#FF8C42";
                  }}
                >
                  <ShoppingBag className="h-4 w-4" />
                  <span>Order Now</span>
                </Link>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleMobileMenu}
                className="p-2 rounded-full transition-colors duration-200"
                style={{ color: "#7B7B7B" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(47, 72, 88, 0.1)";
                  e.currentTarget.style.color = "#2F4858";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "#7B7B7B";
                }}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <motion.div
                  animate={isMobileMenuOpen ? "open" : "closed"}
                  variants={{
                    closed: { rotate: 0 },
                    open: { rotate: 180 },
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </motion.div>
              </motion.button>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu Drawer */}
      <>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={BACKDROP_VARIANTS}
              className="fixed inset-0 z-40 backdrop-blur-sm md:hidden"
              style={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}
              onClick={closeMobileMenu}
            />

            {/* Mobile Menu Drawer */}
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={MOBILE_DRAWER_VARIANTS}
              className="fixed top-0 right-0 h-full w-80 z-50 shadow-2xl md:hidden overflow-y-auto"
              style={{
                backgroundColor: "#FAFAFA",
                borderLeft: "1px solid rgba(47, 72, 88, 0.1)",
                boxShadow: "-10px 0 30px rgba(0, 0, 0, 0.1)",
              }}
              id="mobile-menu"
            >
              {/* Drawer Header */}
              <motion.div
                className="flex items-center justify-between p-4 border-b"
                style={{ borderBottomColor: "rgba(47, 72, 88, 0.1)" }}
                variants={MOBILE_ITEM_VARIANTS}
              >
                <div className="flex items-center gap-3">
                  <Link href="/" className="flex items-center gap-2">
                    <motion.div
                      whileHover={{ scale: 1.05, rotate: 3 }}
                      whileTap={{ scale: 0.95, rotate: -3 }}
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                      }}
                      className="h-15 w-50 rounded-full overflow-hidden flex items-center justify-center shadow-sm"
                    >
                      <Image
                        src="/logo.svg"
                        alt="Inde Menu"
                        width={110}
                        height={110}
                        className="object-contain"
                      />
                    </motion.div>
                  </Link>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05, rotate: 90 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={closeMobileMenu}
                  className="p-2 rounded-full transition-colors duration-200"
                  style={{ color: "#7B7B7B" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(47, 72, 88, 0.1)";
                    e.currentTarget.style.color = "#2F4858";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#7B7B7B";
                  }}
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </motion.button>
              </motion.div>

              {/* Navigation Items */}
              <div className="p-4 space-y-1">
                {NAV_ITEMS.map((item, index) => {
                  const isActive = isActiveLink(item.href);
                  return (
                    <motion.div
                      key={item.name}
                      variants={MOBILE_ITEM_VARIANTS}
                      custom={index}
                    >
                      <Link
                        href={item.href}
                        onClick={closeMobileMenu}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 font-medium"
                        style={{
                          backgroundColor: isActive
                            ? "rgba(47, 72, 88, 0.1)"
                            : "transparent",
                          color: isActive ? "#2F4858" : "#1E1E1E",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor =
                              "rgba(47, 72, 88, 0.05)";
                            e.currentTarget.style.transform = "translateX(4px)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                            e.currentTarget.style.transform = "translateX(0px)";
                          }
                        }}
                        aria-current={isActive ? "page" : undefined}
                      >
                        {item.icon && <item.icon className="h-5 w-5" />}
                        <span className="text-base">{item.name}</span>
                        {isActive && (
                          <motion.div
                            className="ml-auto h-2 w-2 rounded-full"
                            style={{ backgroundColor: "#2F4858" }}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 20,
                            }}
                          />
                        )}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              {/* Drawer Actions */}
              <motion.div
                className="p-4 border-t space-y-3 mt-6"
                style={{ borderTopColor: "rgba(47, 72, 88, 0.1)" }}
                variants={MOBILE_ITEM_VARIANTS}
              >
                <motion.div variants={MOBILE_ITEM_VARIANTS}>
                  <Link
                    href="/login"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 font-medium"
                    style={{ color: "#1E1E1E" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "rgba(47, 72, 88, 0.05)";
                      e.currentTarget.style.transform = "translateX(4px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.transform = "translateX(0px)";
                    }}
                  >
                    <User className="h-5 w-5" />
                    <span className="text-base">Login / Signup</span>
                  </Link>
                </motion.div>

                <motion.div variants={MOBILE_ITEM_VARIANTS}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Link
                      href="/vendors"
                      onClick={closeMobileMenu}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all duration-200 text-white"
                      style={{ backgroundColor: "#FF8C42" }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          "rgba(255, 140, 66, 0.9)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(255, 140, 66, 0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#FF8C42";
                        e.currentTarget.style.transform = "translateY(0px)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      <ShoppingBag className="h-5 w-5" />
                      <span className="text-base">Order Now</span>
                    </Link>
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Drawer Footer */}
              <motion.div
                className="p-4 border-t mt-8"
                style={{ borderTopColor: "rgba(47, 72, 88, 0.1)" }}
                variants={MOBILE_ITEM_VARIANTS}
              >
                <div
                  className="text-center text-xs space-y-2"
                  style={{ color: "#7B7B7B" }}
                >
                  <motion.div
                    className="flex items-center justify-center gap-1"
                    whileHover={{ scale: 1.05 }}
                  >
                    <MapPin className="h-3 w-3" />
                    <span>Lilongwe, Malawi</span>
                  </motion.div>
                  <motion.div
                    className="flex items-center justify-center gap-1"
                    whileHover={{ scale: 1.05 }}
                  >
                    <Phone className="h-3 w-3" />
                    <span>+265 123 456 789</span>
                  </motion.div>
                  <motion.div
                    className="flex items-center justify-center gap-1"
                    whileHover={{ scale: 1.05 }}
                  >
                    <Clock className="h-3 w-3" />
                    <span>Open: 6AM - 11PM</span>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </>
    </>
  );
}
